import { GameCard, Reward, GameType, RewardType } from 'ui/types/Games'
import { Assignment, RewardRecipient } from 'ui/types/GameTypes'
import { CampaignMetric } from './CampaignMetrics'

export interface Ranking {
  isSelf: boolean
  rank: number
  score: number
}

export interface Rules {
  goal: number
  metric: string
  unit: string
}

export interface Winner {
  id: number
  imageUrl: string
  name: string
}

export enum UserVisibilityMode {
  AllUsers = 'all_users',
  TeamUsers = 'team_users',
}

export interface PrizeEntry {
  reward: {
    id: number
    type: RewardType
    value: number
    receivingRank: number
    giftCardName?: string
    giftCardImage?: string
  }
  status: 'projected' | 'earned' | 'fulfilled'
  certainty: 'definite' | 'high' | 'low' | null
  timesEarned: number
  payoutBreakdown: {
    pending: number
    fulfilled: number
  }
}

export interface PrizeStatus {
  entries: PrizeEntry[]
  summary: {
    totalPending: number
    totalFulfilled: number
    hasReward: boolean
  }
}

export interface Player {
  awardCount: number | null
  earnedReward: Reward[] | null
  fulfilledPayout: number
  hasThreshold: boolean
  pendingPayout: number
  progressPercentage: number
  progressReason: string
  ranking: number
  score: number
  threshold: number | null
  totalScore: number
  prizeStatus?: PrizeStatus
}

export enum StatusType {
  Active = 'active',
  Completed = 'completed',
  NeedsVerification = 'needs_verification',
  Upcoming = 'prelaunch',
  Draft = 'draft',
}

export enum SortDirection {
  Asc = 'asc',
  Desc = 'desc',
  None = 'none',
}

export interface Status {
  statusType: StatusType.Active | StatusType.Upcoming | StatusType.Completed
}

export interface GameResponse {
  assign: Assignment
  campaignMetrics?: CampaignMetric[]
  countActivitiesAfterGuide?: boolean
  coverImageUrl?: string
  currentUserScore: number
  expiresAt: Date
  howToWin: string
  id: number
  isSingleGoalReward?: boolean
  isMultiMetric?: boolean
  jackpot?: number
  lastUpdatedAt?: string
  limitWinners?: number
  maximumWinners?: number
  managerViewEnabled: boolean
  name: string
  numberOfWinners?: number
  percentageTimeElapsed: number
  playerCount?: number
  playerInformation: Player
  rankings: Ranking[]
  rewardDistributionRecipient: RewardRecipient
  rewards: Array<{
    type: Reward['type']
    value: any // returns string amount if reward type is tokens OR object containing rarity of chest as property if reward type is chest
  }>
  rewardsAwardedCount?: number
  rules: Rules
  startsAt: Date
  status: Status['statusType']
  score: number
  timeRemaining: string
  type: GameCard['gameType']
  userCount: number
  userVisibilityMode: UserVisibilityMode
  winner?: Winner
}

export enum GameFilters {
  Active = 'active',
  Upcoming = 'upcoming',
  Completed = 'completed',
  NeedsVerification = 'needs_verification',
}

export const GameFilterLabels: Record<GameFilters, string> = {
  [GameFilters.Active]: 'Active',
  [GameFilters.Upcoming]: 'Upcoming',
  [GameFilters.Completed]: 'Completed',
  [GameFilters.NeedsVerification]: 'Awaiting Verification',
}

export const GamePaginationLabels: Record<GameFilters, string> = {
  [GameFilters.Active]: 'Active Games',
  [GameFilters.Upcoming]: 'Upcoming Games',
  [GameFilters.Completed]: 'Completed Games',
  [GameFilters.NeedsVerification]: 'Games Needing Verification',
}

export interface Game {
  assign: Assignment
  awardCount?: number
  awardedAmount?: number
  blodId?: string
  countActivitiesAfterGuide?: boolean
  coverImageUrl?: string
  earnedReward?: Reward[]
  endDateTime?: Date
  fulfilledPayout: number
  gameType: GameCard['gameType']
  goal: number
  hasThreshold: boolean
  id: string
  isCompleted?: boolean
  isCurrentUser?: boolean
  isSingleGoalReward?: boolean
  isUpcoming?: boolean
  lastUpdatedAt?: string
  limitWinners?: number
  metric: string
  name: string
  numberOfWinners?: number
  pendingPayout: number
  place?: number
  prizeAmount: number
  prizeType: Reward['type']
  rewards: Reward[]
  score: number
  startDateTime?: Date
  status?: Status['statusType']
  threshold: number | null
  totalScore: number
  unit: string
  userId?: number
  userImageUrl?: string
  userName?: string
}

export interface NormalizedGame {
  assign: Assignment
  awardCount?: number
  campaignMetrics?: CampaignMetric[]
  countActivitiesAfterGuide?: boolean
  coverImageUrl?: string | null
  earnedReward: Reward[] | null
  endDateTime: Date
  fulfilledPayout: number
  gameType: GameType
  goal: number
  hasManagerView: boolean
  hasPlayerInformation: boolean
  hasThreshold: boolean
  howToWin: string
  id: number
  isMultiMetric?: boolean
  isSingleGoalReward?: boolean
  jackpot?: number
  lastUpdatedAt?: string
  limitWinners?: number
  maximumWinners?: number
  metric: string
  name: string
  numberOfWinners?: number
  pendingPayout: number
  percentageTimeElapsed: number
  place: number
  playerCount?: number
  prizeAmount?: number
  prizeStatus?: PrizeStatus
  prizeType: RewardType
  progressPercentage: number
  progressReason: string
  rewards: Array<Reward>
  rewardsAwardedCount?: number
  rewardDistributionRecipient: RewardRecipient
  rules: Rules
  score: number
  startDateTime: Date
  status: StatusType
  timeRemaining: string
  threshold: number | null
  totalScore: number
  unit: string
  userCount: number
  userId?: number
  userImageUrl?: string
  userName?: string
  userVisibilityMode: UserVisibilityMode
}

export interface GameCreationResponse {
  data: {
    message: 'Campaign has been created'
    gameId: number
  }
}

export const gamesFilterOptions = [...Object.keys(GameFilters)]

export const gamesFilterOptionValues: GameFilters[] = gamesFilterOptions.map(
  value => GameFilters[value],
)

export const gamesFilterOptionsWithLabels = gamesFilterOptionValues.map(
  value => ({
    value,
    name: GameFilterLabels[value],
  }),
)

export const getGamesFilterOptions = () => {
  return gamesFilterOptionValues.map(value => ({
    value,
    name: GameFilterLabels[value],
  }))
}
export interface Rewards {
  claimedAt?: string
  claimedBy?: number
  jackpot?: number
  receivingRank?: number
  receiving_rank: number
  type: string
  value?: string
}
